import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, {
    useEffect,
    useCallback,
    useContext,
    Fragment,
    useReducer,
} from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { modal } from "components/HeadlessModal";
import { apiGet, apiPost, apiPut, messageFromException } from "helpers/Request";
import {
    transform as containerTransformer,
    ContainerObjectSchema,
    prepare as containerPreparer,
} from "schemas/Container";
import EditForm from "./EditForm";
import { useDispatch, useSelector } from "react-redux";
import { addItem as addRecentItem } from "redux/actions/recent-items";
import { useHistory } from "react-router-dom";
import { transform } from "lodash";
import { ContainerContext } from "context/Container";
import { AppContext } from "context/App";
import { toast } from "react-hot-toast";

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

const ensureContainerShape = (container, contentFlow) => {
    const allProps = contentFlow.metaProperties["container"];

    return Object.assign({}, container, {
        meta: transform(
            allProps,
            (result, metaProp, key) => {
                const val = container.metaProperties.find(
                    (mp) => mp.field === metaProp.field
                );
                result[metaProp.field] = val ? val.value : "";
            },
            {}
        ),
        tags: container.tags.map((tag) => {
            return { label: tag.name, value: tag.uuid };
        }),
        workflow: {
            value: contentFlow.workflows[0].id,
            label: contentFlow.workflows[0].name,
        },
        sort_order:
            container.sort_order.length &&
            typeof container.sort_order[0] !== "object"
                ? container.sort_order
                : [],
        content: container.content.map((content) => {
            return { uuid: content.uuid };
        }),
    });
};

const prepareForSaving = (values, container, contentFlow) => {
    const allProps = contentFlow.metaProperties["container"];

    const tags = values.tags.map((tag) => tag.value);
    const content = values.content.map((content) => content.uuid);

    const meta = Object.keys(values.meta)
        .map((key, i) => {
            const type = allProps.find((mp) => mp.field === key);
            return {
                field: key,
                value: values.meta[key],
                type: type ? type.type : "NoopMetaProperty",
            };
        })
        .filter((mp) => mp.value !== "");

    return containerPreparer(
        Object.assign({}, container, values, {
            tags: tags,
            meta: meta,
            workflow_id: values.workflow.value,
            content: content,
            layout_template:
                values.layout_template === "Default"
                    ? null
                    : values.layout_template,
        })
    );
};

const loadContainer = async (uuid, pub, setState) => {
    return apiGet("/v4/container/" + uuid, pub, {
        include: "content,attachment",
    })
        .then((response) => {
            return containerTransformer(response);
        })
        .then((item) => {
            setState({ container: item, loading: false });
            return item;
        });
};

export default function ContainerEditScreen(props) {
    const { uuid } = useParams();
    const location = useLocation();
    const auth = useAuth();
    const dispatch = useDispatch();
    const history = useHistory();

    const [state, setState] = useReducer(reducer, {
        container: Object.assign(ContainerObjectSchema, { type: "automatic" }),
        formValues: Object.assign(ContainerObjectSchema, { type: "automatic" }),
        loading: false,
        saving: false,
        isUserEditable: false,
        preformingStateUpdate: false,
        requestReload: useCallback(() => {
            loadContainer(uuid, auth.publication, setState).catch((err) => {
                modal.error({
                    title: "Unable to load container",
                    message: messageFromException(err),
                });
            });
        }, [uuid, auth.publication]),
    });

    const { setCurrentNav } = useContext(AppContext);

    const contentFlow = useSelector((state) => state.contentFlow);

    const onSubmit = (values) => {
        if (
            state.container.lock &&
            state.container.lock.user.uuid !== auth.user.uuid
        ) {
            console.log("not locked by user");
            return;
        } else if (state.container.uuid && !state.container.lock) {
            console.log("no container lock");
            return;
        }

        const id = toast.loading("Saving...");
        const data = prepareForSaving(values, state.container, contentFlow);

        setState({ saving: true });

        const apiAction = state.container.uuid ? apiPut : apiPost;

        return apiAction(
            "/v4/container/" +
                (state.container.uuid ? state.container.uuid : "") +
                "?include=content,attachment",
            auth.publication,
            data
        )
            .then((resp) => {
                const item = containerTransformer(resp);
                toast.success("Container saved", { id });

                if (!state.container.uuid) {
                    // was a new item, so redirect
                    apiPost("/v4/lock-request", auth.publication, {
                        srn: item.srn,
                    })
                        .then((resp) => {
                            history.push("/app/container/" + item.uuid);
                        })
                        .catch((err) => {
                            toast.error("Unable to acquire lock");
                        });
                }

                setState({ container: item });
                setState({ saving: false });
                setTimeout(() => {
                    setState({ loading: false });
                }, 0);
            })
            .catch((err) => {
                setState({ saving: false });
                toast.dismiss(id);
                modal.error({
                    title: "Unable to save container",
                    children: <Fragment>{messageFromException(err)}</Fragment>,
                });
            });
    };

    const onRequestNavigate = (url) => {
        history.push(url);
    };

    useEffect(() => {
        setState({ loading: true });
        setState({ container: ContainerObjectSchema });
        if (!uuid || uuid === "new") {
            setTimeout(() => {
                setState({ loading: false });
            }, 0);
            return;
        }

        loadContainer(uuid, auth.publication, setState)
            .then((item) => {
                dispatch(addRecentItem(item));
            })
            .catch((err) => {
                modal.error({
                    title: "Unable to load container",
                    message: messageFromException(err),
                });
            });
    }, [location, uuid, auth.publication, dispatch]);

    useEffect(() => {
        // this is a gallery, so it's "content" not a "container"
        setCurrentNav("container");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setState({
                formValues: ensureContainerShape(state.container, contentFlow),
            });
        });
    }, [state.container, contentFlow]);

    useEffect(() => {
        if (
            state.container.uuid &&
            state.container.lock &&
            state.container.lock.user.uuid === auth.user.uuid
        ) {
            setState({ isUserEditable: true });
        } else if (!state.container.uuid) {
            setState({ isUserEditable: true });
        } else {
            setState({ isUserEditable: false });
        }
    }, [state.container, auth.user]);

    // content and form values are provided via context provider
    return (
        <ContainerContext.Provider
            value={{
                state,
                setState,
            }}
        >
            <MainBlock className="z-0">
                <EditForm
                    onSubmit={onSubmit}
                    onRequestNavigate={onRequestNavigate}
                />
            </MainBlock>
        </ContainerContext.Provider>
    );
}
